/**
 * Sticky audio: phrase-linked pronunciation clips for learner text blocks.
 * Learners use entry.audioUrl (pre-baked playback). originalAudioUrl and
 * voiceStylePresetId are admin metadata only; stylization is applied on admin save.
 */

import React from 'react';
import { ContentBlockType } from '../services/contentService';
import {
  findWholeWordOccurrences,
  findWholeWordSpans,
  foldStickyMatchKey,
  foldStickyMatchText,
  spanIsWholeWordInTextNode
} from './stickyAudioWordMatch';

export interface StickyAudioEntry {
  id: string;
  phrase: string;
  audioUrl: string;
  audioFileName?: string;
  textRange?: { start: number; end: number };
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
}

export interface StickyAudioMapEntry {
  phrase: string;
  phraseKey: string;
  audioUrl: string;
  audioFileName?: string;
  vocabId?: number;
  english?: string;
  phonetic?: string;
  example?: string;
  imageUrl?: string;
  partOfSpeech?: number;
  entryKey?: string;
}

export type StickyAudioMap = Record<string, StickyAudioMapEntry>;

export function normalizeStickyAudioPhraseKey(phrase: string): string {
  return phrase.trim().normalize('NFC').toLowerCase();
}

export function parseStickyAudioJson(json?: string | null): StickyAudioEntry[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e: StickyAudioEntry & { OriginalAudioUrl?: string; VoiceStylePresetId?: string | null }) =>
        e &&
        typeof e.id === 'string' &&
        typeof e.phrase === 'string' &&
        typeof e.audioUrl === 'string'
    ).map((e) => ({
      ...e,
      originalAudioUrl: e.originalAudioUrl ?? e.OriginalAudioUrl,
      voiceStylePresetId: e.voiceStylePresetId ?? e.VoiceStylePresetId ?? null
    }));
  } catch {
    return [];
  }
}

export function buildStickyAudioMapFromApi(
  entries: StickyAudioMapEntry[]
): StickyAudioMap {
  const map: StickyAudioMap = {};
  for (const entry of entries) {
    const key = entry.phraseKey || normalizeStickyAudioPhraseKey(entry.phrase);
    if (!key) continue;
    map[key] = entry;
  }
  return map;
}

export type MergeBlockStickyAudioOptions = {
  /** Admin preview may include not-yet-uploaded blob clips so underlines match the editor. */
  includeBlobUrls?: boolean;
};

/** Merge block-level sticky audio JSON into the global learner map. */
export function mergeBlockStickyAudioMap(
  globalMap: StickyAudioMap,
  blockStickyJson?: string | null,
  options?: MergeBlockStickyAudioOptions
): StickyAudioMap {
  const blockEntries = parseStickyAudioJson(blockStickyJson);
  if (blockEntries.length === 0) return globalMap;

  const merged: StickyAudioMap = { ...globalMap };
  for (const entry of blockEntries) {
    // Playback URL only — styled (or original) file already on server.
    if (!entry.audioUrl) continue;
    if (!options?.includeBlobUrls && entry.audioUrl.startsWith('blob:')) continue;
    const key = normalizeStickyAudioPhraseKey(entry.phrase);
    if (!key) continue;
    merged[key] = {
      phrase: entry.phrase,
      phraseKey: key,
      audioUrl: entry.audioUrl,
      audioFileName: entry.audioFileName
    };
  }
  return merged;
}

export function textHasStickyAudioMatches(
  text: string | undefined | null,
  map: StickyAudioMap
): boolean {
  if (!text?.trim() || Object.keys(map).length === 0) return false;
  const normalizedText = text.normalize('NFC');
  return getStickyAudioPhrases(map).some((entry) => {
    const phrase = entry.phrase.trim().normalize('NFC');
    return phrase.length > 0 && findWholeWordOccurrences(normalizedText, phrase).length > 0;
  });
}

export function hasStickyAudioForBlock(
  globalMap: StickyAudioMap,
  blockStickyJson?: string | null,
  textContent?: string | null,
  formattedContentHtml?: string | null,
  options?: MergeBlockStickyAudioOptions
): boolean {
  const effectiveMap = mergeBlockStickyAudioMap(globalMap, blockStickyJson, options);
  if (Object.keys(effectiveMap).length === 0) return false;

  if (textHasStickyAudioMatches(textContent, effectiveMap)) return true;

  if (formattedContentHtml) {
    const plainFromHtml = formattedContentHtml
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (textHasStickyAudioMatches(plainFromHtml, effectiveMap)) return true;
  }

  return false;
}

function readFormattedContentHtml(textFormatting?: string | null): string | undefined {
  if (!textFormatting) return undefined;
  try {
    const parsed = JSON.parse(textFormatting) as { formattedContent?: string };
    return parsed.formattedContent;
  } catch {
    return undefined;
  }
}

/** True when a learner text / text-audio block contains at least one sticky-audio phrase. */
export function textBlockHasStickyAudioWords(
  globalMap: StickyAudioMap,
  options: {
    blockType: ContentBlockType;
    textContent?: string | null;
    audioUrl?: string | null;
    blockStickyJson?: string | null;
    textFormatting?: string | null;
  }
): boolean {
  const { blockType, textContent, audioUrl, blockStickyJson, textFormatting } = options;
  if (blockType !== ContentBlockType.Text && blockType !== ContentBlockType.TextAudio) {
    return false;
  }
  if (blockType === ContentBlockType.Text && !textContent?.trim()) return false;
  if (blockType === ContentBlockType.TextAudio && (!textContent?.trim() || !audioUrl)) return false;
  return hasStickyAudioForBlock(
    globalMap,
    blockStickyJson,
    textContent,
    readFormattedContentHtml(textFormatting)
  );
}

export function getStickyAudioPhrases(map: StickyAudioMap): StickyAudioMapEntry[] {
  return Object.values(map).sort((a, b) => {
    const byKey = foldStickyMatchKey(b.phrase).length - foldStickyMatchKey(a.phrase).length;
    return byKey !== 0 ? byKey : b.phrase.length - a.phrase.length;
  });
}

/**
 * Wrap every NFC case-insensitive occurrence of known phrases in HTML with
 * clickable sticky-audio spans (longest phrases first).
 */
export function injectStickyAudioMarks(html: string, map: StickyAudioMap): string {
  if (!html || !map || Object.keys(map).length === 0) return html;
  if (typeof document === 'undefined') return html;

  const phrases = getStickyAudioPhrases(map);
  if (phrases.length === 0) return html;

  let result = html;
  for (const entry of phrases) {
    const matchKey = foldStickyMatchKey(entry.phrase.trim());
    if (!matchKey) continue;
    const lowered = matchKey.toLowerCase();

    const container = document.createElement('div');
    container.innerHTML = result;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = (node as Text).parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        const tag = parent.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
        if (parent.closest('.sticky-audio-mark, .text-audio-highlight')) {
          return NodeFilter.FILTER_REJECT;
        }
        const text = foldStickyMatchKey(node.textContent || '');
        return text.toLowerCase().includes(lowered)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    const matches: Text[] = [];
    while (walker.nextNode()) {
      matches.push(walker.currentNode as Text);
    }

    matches.forEach((node) => {
      const original = foldStickyMatchText(node.textContent || '');
      const spans = findWholeWordSpans(original, entry.phrase);
      const fragment = document.createDocumentFragment();
      let cursor = 0;
      for (const span of spans) {
        if (span.start < cursor) continue;
        if (!spanIsWholeWordInTextNode(node, container, span, original)) continue;
        if (span.start > cursor) {
          fragment.appendChild(document.createTextNode(original.slice(cursor, span.start)));
        }
        const mark = document.createElement('span');
        mark.className = 'sticky-audio-mark';
        mark.setAttribute('role', 'button');
        mark.setAttribute('tabindex', '0');
        if (entry.audioUrl) mark.setAttribute('data-sticky-audio-url', entry.audioUrl);
        if (entry.vocabId != null) mark.setAttribute('data-word-help-id', String(entry.vocabId));
        mark.setAttribute('aria-label', entry.phrase);
        mark.textContent = original.slice(span.start, span.end);
        fragment.appendChild(mark);
        cursor = span.end;
      }
      if (cursor === 0) return;
      if (cursor < original.length) {
        fragment.appendChild(document.createTextNode(original.slice(cursor)));
      }
      node.parentNode?.replaceChild(fragment, node);
    });

    result = container.innerHTML;
  }

  return result;
}

/** Plain-text React nodes with sticky-audio spans. */
export function renderTextWithStickyAudio(
  text: string,
  map: StickyAudioMap
): React.ReactNode[] {
  const phrases = getStickyAudioPhrases(map);
  if (!text || phrases.length === 0) return [text];

  type Segment = { start: number; end: number; entry: StickyAudioMapEntry };
  const segments: Segment[] = [];
  const normalizedText = text.normalize('NFC');

  for (const entry of phrases) {
    for (const span of findWholeWordSpans(normalizedText, entry.phrase)) {
      const overlaps = segments.some((s) => !(span.end <= s.start || span.start >= s.end));
      if (!overlaps) {
        segments.push({ start: span.start, end: span.end, entry });
      }
    }
  }

  if (segments.length === 0) return [text];

  segments.sort((a, b) => a.start - b.start);
  const nodes: React.ReactNode[] = [];
  let last = 0;
  segments.forEach((seg, i) => {
    if (seg.start > last) {
      nodes.push(
        <React.Fragment key={`t-${last}`}>
          {normalizedText.slice(last, seg.start)}
        </React.Fragment>
      );
    }
    nodes.push(
      <span
        key={`sa-${i}-${seg.start}`}
        className="sticky-audio-mark"
        role="button"
        tabIndex={0}
        {...(seg.entry.audioUrl ? { 'data-sticky-audio-url': seg.entry.audioUrl } : {})}
        {...(seg.entry.vocabId != null ? { 'data-word-help-id': String(seg.entry.vocabId) } : {})}
        aria-label={seg.entry.phrase}
      >
        {normalizedText.slice(seg.start, seg.end)}
      </span>
    );
    last = seg.end;
  });
  if (last < normalizedText.length) {
    nodes.push(
      <React.Fragment key={`t-${last}-end`}>
        {normalizedText.slice(last)}
      </React.Fragment>
    );
  }
  return nodes;
}
